// Brief 57 §3c — THE MATTER-LIST CONFIG, one builder for the two lists that
// were the tree's largest duplicated pair (tier 2 rank 1,
// docs/extraction-candidates.md: 1410 lines at 83.6%).
//
// Laurent's rulings, in force here:
//   57 (2026-08-09)  make EntityList genuinely data-driven FIRST, then
//                    converge the two case lists onto it.
//   54b (verbatim)   "Where CaseView and CollectiveCaseView present the same
//                    section, they run one piece of code parameterised by
//                    view."
//
// WHAT REMAINS DIFFERENT — the brief's own list, realised as parameters:
//   the ENTITY        v.rows (Case vs CollectiveCase), v.metrics, v.statusDate,
//                     v.nextEvDate — computed in the owning view, passed in
//   the ROW-CLICK     v.rowClick (both go to sDid today; the parameter is the
//                     seam the brief names)
//   the STRIP SCOPE   v.stripEvents / v.stripMatterOf / v.stripClick — the
//                     case strip shows EVERY event of the day and opens the
//                     event modal for a case-less one; the collective strip
//                     shows only this list's events
//   plus the export column maps, which name different spreadsheet columns
//   because the two entities hold different parties.
//
// BRIEF 58 §2 CLOSED THE REST, and the four rows were single declarations
// exactly because this builder exists — none needed a per-kind branch:
//   row 11  the My-matters pill, now passed by both views (one label key,
//           `case.filter.my_cases`, whose French is already "Mes dossiers")
//   row 14  Clients, was collective-only
//   row 15  Latest invoice, was collective-only AND the only unsortable
//           header of nineteen — now shared and sorted
//   row 23  the last stage's TYPE, beside the status pill and its date
//   57c-iii RULED — "both should be identical, all RPs": the case list's
//           search haystack wins on both, and the global-query fallback
//           travels with it. The per-kind branch is GONE, not parameterised.
// The partner CELL (multi-badge vs single-badge) WAS the one measured delta
// still kept per kind, on the ground that no ruling named it. Brief 86 §0
// names it — "where they differ today and the difference was nobody's
// decision, CV is authoritative" — so as of §2d there is no partner branch and
// the case list's rendering is the only one. What is left per kind is `am`,
// the add label, and the export sheet.
//
// TWO DIFFERENCES WERE RESOLVED BEFORE THIS FILE EXISTED (55 §0, own
// commits): the collective strip's debug console.log, and the case list's
// Type sort now comparing the label the reader sees (05m's class). TWO ARE
// CONVERGED HERE AND FLAGGED FOR THE GLANCE: the strip's weekend header
// colour (the case side's theme var `var(--tm)` wins over the collective
// side's hardcoded CT.c_4a5060 — §4.9 forbids hardcoded colours), and the
// case strip's stray `{" "}` whitespace children, which the collective side
// never had and which render nothing between flex items.
//
// NOT A COMPONENT — a config factory. It returns data and callbacks for
// EntityList's declared capabilities; nothing here holds a hook, and the
// t() calls are BARE t (destructured from the view's input) so gate 2 keeps
// seeing every key.
import React from "react"; // 169 — react/react-in-jsx-scope is counted per JSX element; this is the rule's own remedy
import type { CSSProperties } from "react";
import { CT, RAD, FS } from "./tokens";
import { pname, ctc, dayStrip } from "../utils";
import { elementIdsOf } from "./counselLinks"; // 214 addendum 2 §18 / addendum 3 §21 — a collective case's party columns are elements
import MoneyStack from "../components/MoneyStack"; // 190 glance round 3 [G] — the seven recap cells, home + foreign
import { BASIS_BAR_BORDER } from "../components/CountersStrip"; // 190 round 11 [BA] — the strip's own bar, as a cell border
import { axisDayOf } from "./scheduleAxis";
import type { usePermissions } from "../hooks/usePermissions"; // 190 round 5 [R] — the column gate's shape
import { InitialsBadge } from "../components/SharedUI";
import { ArchivedHoleBadge } from "../components/SharedUI";
import { HScroll } from "../components/SharedUI"; // 133 §3
import { soleArchived } from "./collaboratorState";
import { stackCut } from "./badgeStack"; // brief 94 §1b
import { resolveTaxonomyLabel } from "./taxonomyHelpers";
import { getStatusBadgeTooltip } from "./invoiceTransitions";
import EventTypeChips from "../components/EventTypeChips";
import { eventTypeVisible } from "./eventTypeFilter";
import {
  matterPassesProspect,
  prospectFilterCounts,
  prospectFilterOptions,
  type ProspectFilterValue,
} from "./prospectFilter"; // 139 §3 — the axis, extracted when it got a second consumer

/* BRIEF 108 §9 — `truncJu` (102 §1b's middle-cut at 23 characters) is GONE,
   not gated: it fired however much blank width the row held, which is the
   defect this § names. The insight it carried — jurisdiction names are
   distinguished by their TAIL ("Tribunal de première instance de BRUXELLES"
   against "… de LIÈGE") — is kept by the stronger means: the whole name
   renders. The matter lists' cells are uncapped (`.elist-matters tbody td`,
   App.css) so the table's auto layout shares real estate by content — a
   column only pays when the TABLE is genuinely overfull, and then it pays
   with `.table-wrap`'s horizontal scroll, never with a cut. A
   measured-overflow gate on a cut would never fire on an uncapped auto
   column, so keeping the function would have been keeping dead code. */

// 58 §2 row 23 — the CURRENT stage is the last entry of the stages array, the
// same `(x.stages || []).slice(-1)[0]` both detail panes already compute
// (CaseView, CollectiveCaseView). One reading, so the list and the pane can
// never name different stages for the same matter.
type StageRow = { type?: string | null };
import type { CustomFieldColumn } from "./customFieldColumns"; // 189 §B
import type { TFn } from "./taxonomyTypes"; // 212 §27 — `t` typed at the seam
const lastStageType = (c: { stages?: StageRow[] | null }): string | null =>
  ((c.stages || []).slice(-1)[0] || {}).type || null;

/* 140 §1.2 — A COLUMN SORTS BY WHAT IT SHOWS. The status cell now draws the
   prospect pill in place of the status while the row is tagged, so the
   comparator has to read the same way; left alone it would scatter the
   prospects through the four real statuses by a value the column no longer
   displays anywhere.

   KEYS, NOT LABELS, and that is an extraction rather than a change: this
   comparator has always compared the raw `status` key (`"pending"`) and never
   the resolved taxonomy label the cell prints ("Suspended"). One more value
   joins the same spelling. `"prospect"` sorts after `active`/`archived`/
   `closed`/`pending` alphabetically, so ascending groups the prospects at the
   bottom — grouped, which is what the ruling asks for; WHERE the group lands
   is alphabetical, exactly as the other four are. */
type StatusSortable = { status?: string | null; isProspect?: boolean | null };
const statusSortKey = (c: StatusSortable): string =>
  c.isProspect ? "prospect" : c.status || "";

/* BRIEF 139 §2.3 — the prospect axis's three states, exported so the views
   type their own state with it. A SECOND AXIS, ORTHOGONAL to the status
   chips: "prospects" and "regular" partition the rows by the boolean tag
   (`Case.isProspect`, unit 1's column), "both" is the ruled default.

   139 §3 — the axis MOVED to lib/prospectFilter.ts when the money views became
   its second consumer; the three parts this file used to spell inline (the
   type, the row predicate, the pill's options and counts) are imported below
   and the type is RE-EXPORTED from here, so `CaseView`/`CollectiveCaseView`'s
   §2 imports keep resolving exactly as they did. A pure extraction: no
   behaviour moved with it. */
export type { ProspectFilterValue };

/* 147 §1.3 — THE STRIP'S FLOOR, DERIVED. It was the literal `1159`, sitting
   beside a `minWidth: 80` on the day cell and a `gap: 3` on the row, with a
   comment explaining that 1159 is the product of the other two ("14 full
   columns of 80 + 13 gaps of 3", 132 §3). Three typed numbers, one of which is
   arithmetic on the other two, is how 132 came to ship `970` for a strip whose
   days had stopped being half-width — the comment was right and the number was
   not. Now the number cannot disagree with its own comment.
   The Dashboard's twin declares the SAME three, at gap 4; that 13px difference
   is 13 gaps × 1px and is REAL, not drift — see the twin's own note. */
const STRIP14_DAYS = 14;
const STRIP14_COL = 80;
const STRIP14_GAP = 3;
const STRIP14_MIN = STRIP14_DAYS * STRIP14_COL + (STRIP14_DAYS - 1) * STRIP14_GAP;

/* 190 glance round 3 [G] — what the seven recap cells read off a served row:
   the Récapitulatif's members over the whole matter (GET /cases `recap`,
   beside `latestInvoice`), home figures in the firm's currency and, when the
   matter holds documents in others, `foreign` per code. 190 round 11 [AZ]
   (under the standing ruling; round 5 [O]'s "every figure incl. VAT" was
   superseded on the strip by round 9 [AV]): the four LEFT members travel as
   the work, fees + costs EX-VAT (`wc`); the three RIGHT as the documents as
   issued, VAT included — the wire picks the member's column, lib/matterRecap
   `recapWire`, and this file computes nothing. `foreign` holds the document
   members only — `invoiced` is entry basis and has none. */
export type RecapWire = Readonly<{
  unmarked: number | null; unbillable: number | null; billable: number | null;
  invoiced: number | null; sent: number | null; paid: number | null; due: number | null;
  foreign?: Readonly<Record<string, Readonly<{ sent: number | null; paid: number | null; due: number | null }>>> | null;
}>;
type RecapRow = Readonly<{ recap?: RecapWire | null }>;
/* 190 round 5 [R] — the host's `can` over a column key: `ColumnGate`, exported
   below beside the gate it types (191 fold-in 2). */
export type RecapMember = "unmarked" | "unbillable" | "billable" | "invoiced" | "sent" | "paid" | "due";
/* the strip's own colours per card (TimeEntryTable.tsx counterCard), the DÛ
   rule the strip's: red while something is outstanding, green at 0 —
   `dueTone` is the one member whose colour reads the value */
/* 190 round 5 [R] (ruled 2026-09-16, "why state the obvious?") — the seven
   headers are the strip's own seven words, plain: "Non marqué", "Facturé",
   "Payé", "Dû" — the "· dossier" / "· matter" suffix round 3 named the basis
   with is gone (new keys, the old retired). ONE RESIDUE, stated rather than
   papered over: on a list row and on a Bento card "Non marqué" is the WHOLE
   MATTER (the Récapitulatif's member), while the strip's "Non marqué" on the
   case screen follows the reader's current filter — the same word, two
   populations, and the header no longer says which. The column keys (`k`)
   stay `recap_<member>`: the template `fields` a firm saved name them. */
/* 190 glance round 11 [AY] (ruled — reverses round 10's [AX]): NO column
   header carries the VAT mention. Round 10 hung "Montant & TVA" under
   ENVOYÉ as a second line, on the two lists, the Bento card and Frais et
   honoraires; Laurent: "it's just ugly — all the headers are off balance
   and it breaks the app harmony". The seven headers are the strip's seven
   plain words again; the boundary between the work and the cash is told by
   the STRIP's own "Envoyé (avec TVA)" (round 9 [AV]) — "people will learn
   when reading the strip" — and, in the tables, by the bar ([BA]). */
/* 190 round 11 [BA] (ruled) — THE BAR GOES INTO THE TABLES: "between the 4
   earning columns and the 3 cash flow columns, as in the strip". The sent
   member's cells — the header (`ths`) and every row (`ts`) — carry the
   strip's own bar (CountersStrip's `BASIS_BAR`, the same width and colour)
   as their left border, so the line runs the table's full height and tells,
   structurally, what the strip's separator tells: left of it the work
   (ex-VAT), right of it the cash (the documents as issued). Frais et
   honoraires draws the same border on the same column. */
/* 191 fold-in 2 [RULED, LAURENT — 191 F.0]: THE REPORTS TABS OBEY THE LISTS'
   COLUMN SWITCHES, through the same gate the lists read and never a second
   spelling. This is that gate, lifted out of buildMatterListCfg where it was
   a local: a column key (`recap_due`, `partner`, `cf_<n>` …) → its
   `furniture.list.<k>` key → the host's `can`; `ref` and `title` carry no
   key (a row cannot lose them); the custom-field columns are one key for
   the family. Unwritten = shown (191 (1)); hidden firm-wide = gone for the
   administrator too (190 (4)) — both decided by `can` itself. The host's
   `colCan` is `columnGateOf(usePermissions())` below: the load window keeps
   every column (`viewer === null`), then `can(k)`. */
export type ColumnGate = ReturnType<typeof usePermissions>["can"];
export const columnGateOf = (perms: { viewer: unknown; can: ColumnGate }): ColumnGate =>
  ((k, opts) => perms.viewer === null || perms.can(k, opts)) as ColumnGate;
const UNGATED_COLS: ReadonlySet<string> = new Set(["ref", "title"]);
export const listColumnKey = (k: string): string => `furniture.list.${k.startsWith("cf_") ? "custom_fields" : k}`;
export const listColumnShown = (colCan: ColumnGate) => (k: string): boolean => UNGATED_COLS.has(k) || colCan(listColumnKey(k));

/* 191 F.2b — EXPORTED: the Reports tabs (Par dossier, Par client) read the
   same seven — keys, tones, the bar on Envoyé — so the lists and the reports
   cannot spell a header or a colour differently. */
export const RECAP_MEMBERS: readonly Readonly<{ m: RecapMember; lk: string; l: string; tone: string; bar?: true }>[] = [
  { m: "unmarked",   lk: "cc.col.matter_unmarked",   l: "Unmarked",   tone: "var(--gn)" },
  { m: "unbillable", lk: "cc.col.matter_unbillable", l: "Unbillable", tone: "var(--rd)" },
  { m: "billable",   lk: "cc.col.matter_billable",   l: "Billable",   tone: "var(--am)" },
  { m: "invoiced",   lk: "cc.col.matter_invoiced",   l: "Invoiced",   tone: "var(--ac)" },
  { m: "sent",       lk: "cc.col.matter_sent",       l: "Sent",       tone: "var(--ts)", bar: true },
  { m: "paid",       lk: "cc.col.matter_paid",       l: "Paid",       tone: "var(--gn)" },
  { m: "due",        lk: "cc.col.matter_due",        l: "Due",        tone: "" },
];
const BAR_CELL = { borderLeft: BASIS_BAR_BORDER } as const;
export const dueTone = (v: number): string => (v > 0 ? "var(--rd)" : "var(--gn)");
const foreignOfMember = (r: RecapWire, m: RecapMember): Readonly<Record<string, number>> | null => {
  if (!r.foreign || m === "unmarked" || m === "unbillable" || m === "billable" || m === "invoiced") return null;
  const out: Record<string, number> = {};
  for (const [code, f] of Object.entries(r.foreign)) { const v = f[m]; if (typeof v === "number" && Number.isFinite(v)) out[code] = v; }
  return Object.keys(out).length ? out : null;
};
const RECAP_COLS = RECAP_MEMBERS.map(({ m, lk, l, tone, bar }) => ({
  k: `recap_${m}`,
  lk,
  l,
  ...(bar ? { ths: BAR_CELL, ts: BAR_CELL } : {}),
  sv: (c: RecapRow) => c.recap?.[m] ?? null,
  r: (c: RecapRow) => {
    const v = c.recap?.[m];
    if (v == null) return <span style={{ color: "var(--tm)" }}>—</span>;
    return (
      <span className="mono" style={{ whiteSpace: "nowrap", color: m === "due" ? dueTone(v) : tone }}>
        <MoneyStack home={v} foreign={c.recap ? foreignOfMember(c.recap, m) : null} />
      </span>
    );
  },
}));

/* 201 §A — the two fields the « Associé » stack reads off a responsible
   partner: who he is (the viewer's slot) and whether he is archived (75 §3's
   earmark, `soleArchived`'s own `CoLike`). Named so the partition below is
   typed at its seam and costs the ratchets nothing. */
type RpBadgeLike = { id: number; status?: string | null };

/** 212 §27 [RULED 30] — the matters' subfolder check's control: declared when
 *  the host hands its opener, else none (the toolbar as it was). */
const folderCheckCtl = (t: TFn, onOpen: (() => void) | null | undefined) =>
  onOpen
    ? {
        label: t("folder_fill.check_cases", { fallback: "Check the matters' subfolders" }),
        title: t("folder_check.cases_tip", {
          fallback: "The matters you may manage: a SharePoint folder not found at its address, subfolders missing — one dry run, one repair",
        }),
        onOpen,
      }
    : null;

export const buildMatterListCfg = (v: any) => {
  /* 139 §2 (rule 4) — the two new inputs, typed although `v` is `(v: any)`:
     read off `v` directly so the annotation sits on the declaration.
     `prospectFilter` is the toggle's state, held by the OWNING VIEW exactly as
     `myToggle`'s is; `onAddProspect` is the second create button's action —
     absent, the toolbar renders exactly what it rendered before (the cc list
     passes none: its create-prospect button is unit 8's, by the modal's own
     comment at CollectiveCaseView.tsx:2617-2619). */
  const prospectFilter: {
    value: ProspectFilterValue;
    onChange: (next: ProspectFilterValue) => void;
  } | null = v.prospectFilter || null;
  const onAddProspect: (() => void) | null = v.onAddProspect || null;
  /* 212 §27 [RULED 30] — the matters' subfolder check: handed by the host only
     when the person may run it (App decides — docs/212-deploy-note.md §8.7);
     absent, the toolbar renders exactly what it rendered before. */
  const { onFolderCheck } = v as { onFolderCheck?: (() => void) | null };
  /* 189 §B — the custom-field columns the owning view built for its kind
     (src/lib/customFieldColumns.tsx); appended after the collective extras. */
  const customFieldCols: CustomFieldColumn[] = (v as { customFieldCols?: CustomFieldColumn[] }).customFieldCols ?? [];
  /* 190 round 5 [R] — THE COLUMN SWITCHBOARD, on §F.2's furniture mechanism:
     one single-bit key per column, `furniture.list.<k>` (sectionRegistry.js),
     hidden firm-wide or withheld from a role in the screen the admin already
     has. The host hands its `can` (usePermissions) and the load window keeps
     every column (`viewer === null` → true, the furniture rule); the columns
     a row cannot lose — `ref` (its identity) and `title` (the link that opens
     it) — carry no key and are never asked. The custom-field columns are one
     key for the family. */
  const colCanRaw: unknown = (v as { colCan?: unknown }).colCan;
  const colCan = typeof colCanRaw === "function" ? (colCanRaw as ColumnGate) : ((): boolean => true);
  const columnShown = listColumnShown(colCan);
  const {
    kind, // "ca" | "cc"
    t,
    fd,
    fc,
    rows,
    ju,
    co,
    currentUser,
    metrics,
    nextEvDate,
    statusDate,
    todayStr,
    sort,
    setSort,
    chipsValue,
    onChips,
    myToggle, // null | { active, onToggle, count } — the My-matters pill, both lists (58 §2 row 11)
    searchText,
    onSearch,
    searchExtra, // the global sidebar query, folded into the haystack on both (57c-iii)
    locked,
    onLockToggle,
    onImportOpen,
    canCreate,
    onAdd,
    caseTypeEntries,
    caseStatusEntries,
    caseStageEntries, // 58 §2 row 23 — resolves the last stage's label
    // 58 §5b — the firm's axis. Its `working` flag decides the day-label
    // colour on the strip below.
    // 132 §3 — its `width` is no longer read here: the strip draws every day
    // at a full column, and the setting keeps its effect on PlacesView. The
    // sentence that stood here described the `flex: isWknd ? 1 : 2` this strip
    // used to carry, which is exactly what the ruling removed.
    schedAxis,
    // 132 §6 — the firm's legal holidays, resolved once in App. Optional: a
    // caller that does not pass one draws no holiday and nothing throws.
    holidays,
    // 133 §4 — the firm's calendar-display switches, resolved once in App.
    // Only `stripNonWorking` is this file's business. OPTIONAL, and a caller
    // that omits it gets the ruled default (shading ON) rather than an
    // undefined that would read as "off" — a missing prop must not silently
    // turn a ruling off.
    calDisp,
    // 58 §4 — the event-type chips, on the strip. `agTypeFilt` is App's
    // PERSISTED `agendaTypeFilter` preference — the same one the Agenda view
    // drives — so a type switched off on one of the four surfaces is switched
    // off on all of them, and the choice survives a reload.
    evtTypes,
    agTypeFilt,
    setAgTypeFilt,
    canSeeFin, // gates the Latest-invoice column; both lists since 58 §2 row 15
    getInvoiceHeaderLabel,
    stripEvents,
    stripMatterOf,
    stripClick,
    navAgenda,
    rowClick,
  } = v;
  const cc = kind === "cc";

  // ── the filter chain, verbatim from the two views ─────────────────────────
  const filtered = () => {
    const sq = (searchText || searchExtra || "").toLowerCase();
    return rows
      .filter((c: any) =>
        chipsValue === "all" ? c.status !== "archived" : c.status === chipsValue,
      )
      .filter(
        (c: any) =>
          !myToggle ||
          !myToggle.active ||
          !currentUser ||
          (c.responsiblePartners || []).includes(currentUser),
      )
      /* 139 §2.3 — the prospect arm, beside the my-arm it copies. Orthogonal
         to the chips: a row must pass BOTH axes. (139 §3: the two lines this
         arm used to spell are now `matterPassesProspect`, shared with the three
         money views — same predicate, same answers, one place.) */
      .filter((c: { isProspect?: boolean | null }) =>
        matterPassesProspect(c, prospectFilter?.value),
      )
      .filter((c: any) => {
        if (!sq) return true;
        const jn = ju.find((j: any) => j.id === c.juId)?.name || "";
        // 57c-iii RULED 2026-08-10 — "both should be identical, all RPs". The
        // case behaviour wins on both: the haystack joins EVERY responsible
        // partner's name, not the first. The GLOBAL-QUERY FALLBACK travels with
        // it (see `searchExtra` above): the collective list now answers the
        // sidebar's search box the way the case list always has, so typing a
        // partner's name in the sidebar filters both lists. `q` was already
        // being handed to CollectiveCaseView and destructured there, unused.
        const rpN = (c.responsiblePartners || [])
          .map((_id: any) => {
            const _r = co.find((x: any) => x.id === _id);
            return _r ? pname(_r) : "";
          })
          .join(" ");
        const coNames = (c.caseCollaborators || [])
          .map((x: any) => {
            const m = co.find((z: any) => z.id === x.coId);
            return m ? pname(m) : "";
          })
          .join(" ");
        return (c.title + c.ref + (c.ctype || "") + jn + rpN + coNames)
          .toLowerCase()
          .includes(sq);
      });
  };

  // ── the comparators, one table — ctype resolves the LABEL on both sides
  //    since §3c pre-2 closed the case list's raw-code sort (05m's class) ────
  const rowsSource = () => {
    const fi = filtered();
    const k = sort.k;
    const d = sort.d;
    return [...fi].sort((a: any, b: any) => {
      if (k === "nextev") {
        const ea = nextEvDate(a);
        const eb = nextEvDate(b);
        if (!ea && !eb) return 0;
        if (!ea) return 1;
        if (!eb) return -1;
        return d * ea.localeCompare(eb);
      }
      if (k === "ref") return d * (a.ref || "").localeCompare(b.ref || "");
      if (k === "title") return d * (a.title || "").localeCompare(b.title || "");
      if (k === "type" || k === "ctype")
        return (
          d *
          String(resolveTaxonomyLabel(a.ctype, caseTypeEntries) || "").localeCompare(
            String(resolveTaxonomyLabel(b.ctype, caseTypeEntries) || ""),
          )
        );
      if (k === "ju") {
        const ja = ju.find((j: any) => j.id === a.juId)?.name || "";
        const jb = ju.find((j: any) => j.id === b.juId)?.name || "";
        return d * ja.localeCompare(jb);
      }
      if (k === "partner") {
        const pa = co.find((x: any) => x.id === (a.responsiblePartners || [])[0]);
        const pb = co.find((x: any) => x.id === (b.responsiblePartners || [])[0]);
        return d * pname(pa || {}).localeCompare(pname(pb || {}));
      }
      if (k === "assigned") {
        const aa = (a.caseCollaborators || []).length;
        const ab = (b.caseCollaborators || []).length;
        return d * (aa - ab);
      }
      if (k === "opened")
        return d * (a.openDate || "").localeCompare(b.openDate || "");
      if (k === "tasks") {
        return d * (metrics(a).openTasks - metrics(b).openTasks);
      }
      if (k === "events") {
        return d * (metrics(a).upcomingEvts - metrics(b).upcomingEvts);
      }
      if (k === "clients") {
        return d * (metrics(a).numClients - metrics(b).numClients);
      }
      // 58 §2 row 23 — the LAST stage's type, sorted by the label the reader
      // sees (05m's class, the same rule the Type column got at 57 §3c pre-2).
      if (k === "stage") {
        const la = String(
          resolveTaxonomyLabel(lastStageType(a), caseStageEntries) || "",
        );
        const lb = String(
          resolveTaxonomyLabel(lastStageType(b), caseStageEntries) || "",
        );
        if (!la && !lb) return 0;
        if (!la) return 1; // a matter with no stage sorts last, either way
        if (!lb) return -1;
        return d * la.localeCompare(lb);
      }
      // 58 §2 row 15 — Latest invoice was the only unsortable header of the
      // nineteen. Ordered by the same key the server picks the row with
      // (issueDate desc, id desc), so the column and its source agree; a
      // matter with no invoice sorts last whichever way the arrow points.
      if (k === "latestinv") {
        const ia = a.latestInvoice;
        const ib = b.latestInvoice;
        if (!ia && !ib) return 0;
        if (!ia) return 1;
        if (!ib) return -1;
        const sa = String(ia.issueDate || "");
        const sb = String(ib.issueDate || "");
        return d * (sa.localeCompare(sb) || (ia.id || 0) - (ib.id || 0));
      }
      // 140 §1.2 — through `statusSortKey`, so the sort and the cell name the
      // same thing on a tagged row. See the helper at module scope.
      if (k === "status")
        return d * statusSortKey(a).localeCompare(statusSortKey(b));
      if (k === "statusdate") {
        const sa = String(statusDate(a) || "");
        const sb = String(statusDate(b) || "");
        if (!sa && !sb) return 0;
        if (!sa) return 1; // a row with no status date sorts last, either way
        if (!sb) return -1;
        return d * sa.localeCompare(sb);
      }
      // 55 row 22 — the seven financial comparators went with their columns.
      // The case list's sort is persisted (App prefs.caseSort), so a saved
      // {k:"margin"} must keep falling through to 0 rather than crash.
      return 0;
    });
  };

  // ── the columns — one table, the collective extras appended ───────────────
  const colsT = [
    {
      k: "ref",
      lk: "cc.col.ref",
      l: "Ref",
      /* 140 §1.2 — THE TAG IS GONE FROM HERE, and this cell is 139 §2.4's
         predecessor restored byte for byte (781c54cd). 139 put the tag beside
         the ref because the table is dense and a tag is not a column; the cost
         was that this column then sized itself to fit `0/0002 Prospect`, so it
         read as an unheaded column of its own — which is what Laurent asked
         about. The tag moves to the STATUS cell below, where the ruling puts
         it. Removing it here is the whole fix for the width: no mirror, and
         nothing else about the ref column is touched. */
      r: (c: any) => (
        <span className="mono" style={{ whiteSpace: "nowrap" }}>
          {c.ref}
        </span>
      ),
    },
    {
      k: "partner",
      lk: "cc.col.partner",
      l: "Partner",
      // Brief 86 §2d — THE PER-KIND BRANCH IS GONE, and §0 is the ruling this
      // file's own header said was missing: "the partner CELL (multi-badge vs
      // single-badge) is the one measured delta still kept per kind: it is a
      // rendering, not a behaviour, and NO RULING NAMES IT." §0 names it —
      // where they differ and the difference was nobody's decision, CV is
      // authoritative — so the case list's rendering is now the only one.
      //
      // What the collective list gains: every responsible partner instead of
      // the first, overlapped at -6 with a --bd outline, and 75 §3's
      // ArchivedHoleBadge earmark when all of them are archived. The collective
      // side showed one badge and no earmark, so a collective case whose sole
      // RP had been archived read as normally staffed.
      r: (c: any) => {
        const rpsRaw = (c.responsiblePartners || [])
          .map((_id: any) => co.find((x: any) => x.id === _id))
          .filter(Boolean) as RpBadgeLike[];
        /* 201 §A [RULED — Laurent, 2026-09-20/21, with the screenshot: the list
           Bento] — THE VIEWER'S SLOT IS THE LAST, BECAUSE THE LAST IS THE FRONT.
           This stack paints later-in-front (DOM order, no z-index — see the
           comment on the badge below), and `stackCut` masks every badge that has
           one AFTER it. So a viewer who was RP #1 beside a second RP was the
           badge BEHIND and the badge MASKED: his `--ac` ring — the only thing a
           badge paints outside its border box (badgeStack.ts BADGE_SELF_RING) —
           was cut on its right by the hole and covered by his neighbour.
           MEASURED on staging as user 1 on SMK-09 (RPs 1, 2): TA ringed, TP in
           front, the ring open on the right; the same cell on the list Bento's
           card. THE RULE, at the stack's own definition: a ringed badge is never
           the one painted behind and never the one `stackCut` masks. So the
           record's order stands for everyone else and the viewer goes LAST —
           the front slot of a later-in-front stack, and the one slot the
           `_i < length - 1` mask condition never reaches. Nothing `stackCut`'s
           three numbers depend on moves: 22 / 6 / −6 as before, the hole still
           on the RIGHT of every badge that has one after it.
           The « Assigné » stack below needs NO such move: it sorts the viewer
           FIRST and paints earlier-in-front (`zIndex: sortedAssigns.length -
           i`), so its front slot is already his — the two stacks hold one
           invariant with mirrored orders. */
        const rps2 = [
          ...rpsRaw.filter((_p) => _p.id !== currentUser),
          ...rpsRaw.filter((_p) => _p.id === currentUser),
        ];
        return rps2.length > 0 ? (
          <div style={{ display: "flex" }}>
            {rps2.map((_p: any, _i: any) => (
              <InitialsBadge
                key={_p.id}
                person={_p}
                isMe={_p.id === currentUser}
                size={22}
                fontSize={9}
                /* BRIEF 94 §1b — the `--bd` outline goes here too, and it is
                   MEASURED rather than assumed collateral: §1c named this
                   renderer's two siblings because they sit on a card and a row,
                   but a browser reading the list itself gives `--bd` =
                   rgb(240,242,245) against this table's own surface,
                   rgb(232,234,237). The ring is visible here as well — the case
                   list only ever LOOKED like the page. `stackCut` cuts the
                   overlap instead of painting it, so the surface shows through
                   whatever the surface is, and the viewer's `--ac` ring is once
                   again the only ring in the column. This stack paints
                   later-in-front (no z-index anywhere), so the hole is on the
                   RIGHT of every badge that has one after it. */
                style={{
                  ...(_i > 0 ? { marginLeft: -6 } : null),
                  ...(_i < rps2.length - 1
                    ? stackCut({ size: 22, overlap: 6, side: "right" })
                    : null),
                }}
              />
            ))}
            {/* 75 §3 — every RP archived: the earmark. */}
            {soleArchived(rps2) && (
              <ArchivedHoleBadge size={22} fontSize={9} style={{ marginLeft: 2 }} />
            )}
          </div>
        ) : (
          <span style={{ color: "var(--tm)" }}>—</span>
        );
      },
    },
    {
      k: "title",
      lk: "cc.col.title",
      l: "Title",
      r: (c: any) => {
        /* BRIEF 102 §1a — the cut goes; the cell already ellipsises. Every
           `tbody td` in this app is `max-width: 28ch; overflow: hidden;
           text-overflow: ellipsis; white-space: nowrap` (App.css:539-548), so
           the twenty-character cut fired first and made the column's width
           irrelevant. `title={c.title}` still carries the whole string. */
        return (
          <span
            className="link"
            style={{ fontWeight: 500 }}
            onClick={() => rowClick(c)}
            title={c.title}
          >
            {c.title}
          </span>
        );
      },
    },
    {
      k: "ctype",
      lk: "cc.col.type",
      l: "Type",
      r: (c: any) =>
        c.ctype ? (
          <span
            className="tag"
            style={{
              fontSize: FS.s10,
              padding: "2px 7px",
              whiteSpace: "nowrap",
              ...(ctc(c.ctype, caseTypeEntries) || {}),
            }}
          >
            {resolveTaxonomyLabel(c.ctype, caseTypeEntries)}
          </span>
        ) : (
          <span style={{ color: "var(--tm)" }}>—</span>
        ),
    },
    {
      k: "ju",
      lk: "cc.col.jurisdiction",
      l: "Jurisdiction",
      r: (c: any) => {
        const jr2 = ju.find((j: any) => j.id === c.juId);
        return (
          <span style={{ fontSize: FS.s11 }} title={jr2 ? jr2.name : ""}>
            {jr2 ? jr2.name || "—" : "—"}
          </span>
        );
      },
    },
    {
      k: "opened",
      lk: "cc.col.opened",
      l: "Opened",
      r: (c: any) => (
        <span className="mono" style={{ whiteSpace: "nowrap" }}>
          {fd(c.openDate)}
        </span>
      ),
    },
    {
      k: "assigned",
      // 58 §3 — one key per concept. `cc.col.assigned` and `case.field.assigned`
      // were the same word in all three languages; a COLUMN key doing a field
      // label's job is not the survivor, so the field key serves both.
      lk: "case.field.assigned",
      l: "Assigned",
      r: (c: any) => {
        const assigns = (c.caseCollaborators || [])
          .map((x: any) => co.find((m2: any) => m2.id === x.coId))
          .filter(Boolean);
        const sortedAssigns = [...assigns].sort((a: any, b: any) =>
          a.id === currentUser ? -1 : b.id === currentUser ? 1 : 0,
        );
        return (
          <div style={{ display: "flex" }}>
            {sortedAssigns.map((a2: any, i: any) => {
              const isMe = a2.id === currentUser;
              const isExt = a2.coType === "external";
              return (
                <InitialsBadge
                  key={a2.id}
                  person={a2}
                  isMe={isMe}
                  size={22}
                  fontSize={9}
                  title={pname(a2)}
                  style={{
                    fontWeight: 600,
                    border: isExt
                      ? "1.5px dashed var(--br)"
                      : "1px solid var(--br)",
                    marginLeft: i > 0 ? -6 : 0,
                    position: "relative",
                    zIndex: sortedAssigns.length - i,
                  }}
                />
              );
            })}
            {/* 75 §3 — the whole assigned team archived: the earmark. */}
            {soleArchived(sortedAssigns) && (
              <ArchivedHoleBadge size={22} fontSize={9} style={{ marginLeft: 2 }} />
            )}
            {!assigns.length && <span style={{ color: "var(--tm)" }}>—</span>}
          </div>
        );
      },
    },
    {
      k: "tasks",
      lk: "cc.col.tasks",
      l: "Tasks",
      r: (c: any) => (
        <span style={{ color: "var(--tp)", textAlign: "center" as const, display: "block" }}>
          {metrics(c).openTasks || "—"}
        </span>
      ),
    },
    {
      k: "events",
      lk: "cc.col.events",
      l: "Events",
      r: (c: any) => (
        <span style={{ color: "var(--tp)", textAlign: "center" as const, display: "block" }}>
          {metrics(c).upcomingEvts || "—"}
        </span>
      ),
    },
    {
      k: "status",
      lk: "cc.col.status",
      l: "Status",
      // 55 row 6 — same lookup as ever, both lists.
      /* 140 §1.2 [RULED, Laurent, 2026-08-27] — AND THE PROSPECT PILL REPLACES
         IT while the row is tagged. It does not sit beside it: the lifecycle
         reads Prospect > Active > Suspended > Closed > Archived, so this cell
         answers "what stage is this matter at" with one pill, and a prospect's
         stage is Prospect. The tag is tested FIRST, so a tagged row never
         reaches the status lookup — nor the em-dash, which is why the column
         can be read as "is this a real case yet" on every row.

         `isProspect` stays a boolean column and enters no taxonomy (brief 04
         ruling 1 stands): this is display only, and `.status-prospect` is
         `.status-archived`'s own recipe over `--pp` (App.css, beside the four
         siblings). The label is 139's already-seeded `matters.prospect.badge`
         — §1 introduces no new user-visible string.

         THE STATUS CHIPS ARE UNTOUCHED (brief 04 ruling 2): a prospect whose
         underlying status is `active` still counts under the Active chip while
         this cell reads Prospect. That is the two-axis design, and it is
         Laurent's to rule at the glance. */
      r: (c: any) =>
        c.isProspect ? (
          <span className="status status-prospect">
            {t("matters.prospect.badge", { fallback: "Prospect" })}
          </span>
        ) : c.status ? (
          <span
            className={`status ${({ active: "status-active", pending: "status-pending", closed: "status-closed", archived: "status-archived" } as any)[c.status] || "status-active"}`}
          >
            {resolveTaxonomyLabel(c.status, caseStatusEntries) || c.status}
          </span>
        ) : (
          <span style={{ color: "var(--tm)" }}>—</span>
        ),
    },
    {
      k: "statusdate",
      lk: "cc.col.status_date",
      l: "Status date",
      // 55 §3 row 21 — the CURRENT status's own date. Active shows a dash:
      // its date is the Opening date, a few columns left.
      r: (c: any) => (
        <span className="mono" style={{ whiteSpace: "nowrap" }}>
          {statusDate(c) ? (
            fd(statusDate(c))
          ) : (
            <span style={{ color: "var(--tm)" }}>—</span>
          )}
        </span>
      ),
    },
    // 58 §2 row 23 — the LAST stage's type, beside the status pill and its
    // date, on BOTH lists. One declaration; the taxonomy resolves the label
    // and TaxonomyOrphanMark's job (flagging a code the taxonomy no longer
    // holds) stays in the detail pane — a list cell renders the code itself
    // rather than nothing, which is the 45c behaviour and not a silent blank.
    {
      k: "stage",
      // The word already has a key in the modal vocabulary; §3's rule is one
      // key per concept, so this column reuses it rather than minting a
      // `cc.col.stage` that would need three more seeded rows to say "Stage".
      lk: "field.stage",
      l: "Stage",
      r: (c: any) => {
        const st = lastStageType(c);
        return st ? (
          <span style={{ fontSize: FS.s10, whiteSpace: "nowrap" }}>
            {resolveTaxonomyLabel(st, caseStageEntries) || st}
          </span>
        ) : (
          <span style={{ color: "var(--tm)" }}>—</span>
        );
      },
    },
    // 58 §2 row 14 — Clients on BOTH lists. It was collective-only; the case
    // list has always had the number (`clIds`, the Case wire alias) and no
    // column to put it in. §1 had to land first, or this shipped the same "—".
    {
      k: "clients",
      lk: "cc.col.clients",
      l: "Clients",
      r: (c: any) => (
        <span style={{ color: "var(--tp)", textAlign: "center" as const, display: "block" }}>
          {metrics(c).numClients || "—"}
        </span>
      ),
    },
    // 58 §2 row 15 — Latest invoice on BOTH lists, and sortable on both. The
    // financial gate is unchanged and still per view: a viewer who cannot read
    // invoices gets no column at all, exactly as on the collective list today.
    ...(canSeeFin
      ? [
          {
            k: "latestinv",
            lk: "cc.col.latest_invoice",
            l: "Latest invoice",
            r: (c: any) =>
              c.latestInvoice ? (
                (() => {
                  const li = c.latestInvoice;
                  // 179 C.3 — the `billitSubmittedAt` purple arm STOOD HERE (a
                  // state no live path reached).
                  const clr = li.paymentStatus === "paid"
                      ? "var(--gn)"
                      : li.paymentStatus === "partially_paid"
                        ? "var(--am)"
                        : li.paymentStatus === "unpaid"
                          ? "var(--am)"
                          : // 180 §6 — the `ready` arm stood here; no path reaches it (179 C)
                            li.paymentStatus === "cancelled"
                            ? "var(--rd)"
                            : "var(--tm)";
                  return (
                    <span
                      title={getStatusBadgeTooltip(li)}
                      style={{
                        display: "inline-flex",
                        alignItems: "center",
                        gap: 6,
                        fontSize: FS.s10,
                        whiteSpace: "nowrap",
                      }}
                    >
                      <span
                        style={{
                          fontWeight: 600,
                          letterSpacing: ".3px",
                          color: clr,
                        }}
                      >
                        {getInvoiceHeaderLabel(li)}
                      </span>
                      {li.totalAmount != null && (
                        <span className="mono" style={{ color: "var(--ts)" }}>
                          {fc(li.totalAmount, li.currency)}
                        </span>
                      )}
                    </span>
                  );
                })()
              ) : (
                <span style={{ color: "var(--tm)" }}>—</span>
              ),
          },
        ]
      : []),
    // 190 glance round 2 [C] → round 3 [G], RULED — THE SEVEN COUNTERS, OVER
    // THE WHOLE MATTER: NON MARQUÉ · NON FACTURABLE · FACTURABLE · FACTURÉ ·
    // ENVOYÉ ┃ PAYÉ · DÛ, served on the row as `recap` (routes/cases.js and
    // collectiveCases.js through lib/matterRecap.js — the Récapitulatif
    // financier's own members, `computeFinancials` called on the browser's
    // own body assembled server-side; Laurent: "all 7 over the whole
    // matter", "just add the columns of the case list"). THE BASIS IS NAMED
    // IN THE HEADER: the strip's five row-based cards run over the reader's
    // current filter; these run over the whole matter, so each header says
    // "· dossier" / "· matter" and the two are never read as one figure.
    // Under the same list-level gate; null (the viewer may not read it)
    // renders as the dash, zero as the amount; numeric sort on the home
    // figure; the other currencies stack under it through MoneyStack (169
    // R7 — no sum adds two currencies). The list Bento's card offers every
    // one as a field (crmDefinitions BENTO_CASE_FIELDS) and renders it
    // through these very cells.
    ...(canSeeFin ? RECAP_COLS : []),
    // 189 §B — after the built-in columns and the collective extras.
    ...customFieldCols,
  ];

  // ── the 14-day strip — 54b's "one piece of code parameterised by view" ────
  const preList = () => {
    const _dnFmt14 = new Intl.DateTimeFormat(
      (window as any).__lxLang === "fr"
        ? "fr-BE"
        : (window as any).__lxLang === "nl"
          ? "nl-BE"
          : "en-GB",
      { weekday: "short" },
    );
    const now14 = new Date();
    const days14 = dayStrip(now14);
    // 58 §4 — the chip set needs the events IN SCOPE, not per day: a code the
    // taxonomy no longer holds earns a chip from the events that still carry
    // it, and a strip built day by day would give it one only on the days it
    // appears. Unfiltered on purpose — a type switched off must keep its chip
    // to be switched back on.
    const stripAll14 = days14.flatMap((d: Date) =>
      stripEvents(d.toISOString().slice(0, 10)),
    );
    return (
      <div className="detail-card" style={{ marginBottom: 10 }}>
        <h4
          style={{
            margin: "0 0 8px",
            fontSize: FS.s11,
            color: "var(--tm)",
            textTransform: "uppercase",
            letterSpacing: ".4px",
            fontWeight: 600,
          }}
        >
          {t("cc.14day_view")}
        </h4>
        <EventTypeChips
          evtTypes={evtTypes}
          events={stripAll14}
          value={agTypeFilt}
          onChange={setAgTypeFilt}
          t={t}
          style={{ marginBottom: 6 }}
        />
        {/* 133 §3 — THE STRIP SCROLLS SIDEWAYS NOW, and the `minWidth` on the
            row below is why it had to: 1159 inside a column narrower than that
            CLIPPED the last days with no way to reach them. `HScroll` is the
            modals' own `useScrollHint`, widened to the horizontal axis rather
            than forked, so the ◂ ▸ hints appear at whichever end has more —
            the same discs, the same silence, the same `aria-hidden`. */}
        <HScroll>
        {/* 132 §3 — 14 full columns of 80 + 13 gaps of 3. It was 970 for a
            strip whose four non-working days were half width. 147 §1.3 —
            and now it IS that sentence: `STRIP14_MIN` is computed from the
            same three constants the cells below read, so the floor and the
            column can no longer drift apart. */}
        <div style={{ display: "flex", gap: STRIP14_GAP, minWidth: STRIP14_MIN }}>
          {days14.map((d) => {
            const ds = d.toISOString().slice(0, 10);
            const dw = d.getDay();
            const _ax = axisDayOf(schedAxis, dw);
            const isWknd = !_ax.working;
            /* 132 §6 — a legal holiday of an observed country draws EXACTLY as
               a non-working day does: the same background (133 §2 — see below),
               the muted label colour, and the name on HOVER rather than printed
               in the cell, which a 14-day strip has no room for. Where a
               holiday falls on an already non-working day nothing changes,
               which falls out of the `||` rather than needing a rule. */
            const _hol = holidays ? holidays.on(ds) : [];
            const isHol = _hol.length > 0;
            const _holName = _hol.map((h: { name: string }) => h.name).join(" · ");
            const isOff = isWknd || isHol;
            /* 133 §4 — the switch governs the SHADING and only the shading.
               `isOff` keeps deciding the label colour and the hover title, so
               a firm that turns this off gets back exactly what 132 §3(b) drew
               — the muted label alone — rather than a strip on which a weekend
               has become invisible. `!== false`, not a truthiness test: a
               caller that passes no `calDisp` at all must keep the ruled
               default (on), because a missing prop may not silently turn a
               ruling off. */
            const shadeOff = isOff && calDisp?.stripNonWorking !== false;
            /* 132 §3 — `_ax.width` WAS read here and is not any more. RULED
               (Laurent, 23 Aug 2026): a non-working day is drawn at the same
               width as a working one on the 14-day strips. The strip IGNORES
               width rather than the resolver defaulting it to 2, because a
               firm that has already saved `width: 1` for Saturday would keep
               the halved column a changed default never reaches — and because
               `width` still means something on the surface this ruling does
               not name (PlacesView.tsx:1386, the availability overview, whose
               30-day span is where a half column is a density choice). So the
               setting keeps its editor and its effect; this strip stops
               consuming it.

               ── 133 §2 — AND §3(b) IS WITHDRAWN IN PLACE ──────────────────
               132 §3(b) read "a non-working day carries NO BACKGROUND on these
               strips — no tint, no band; the label alone distinguishes it",
               and this comment said `working` decided the label colour and
               that this was "the only thing that marks the day". IT IS
               WITHDRAWN, and it is withdrawn here rather than left to argue
               with the code below.

               Laurent, correcting the reading: "the idea is just to make the
               cell contrasting in harmony with the chosen theme: lighter in
               night, darker if light". That is `--nwd` exactly — the derived
               token 132 §7 built for the agenda, which his own screenshots
               confirm works there. The strips got the other half of the
               ruling; in the light theme the fourteen columns were
               indistinguishable from one another.

               So: ONE RULE ACROSS BOTH SURFACES, no new token and no literal.
               `isOff` is already the set the agenda paints — a non-working
               weekday or an observed holiday — and it now gets the same field.
               THE WIDTH RULING ABOVE STANDS UNTOUCHED: only the background
               changes, `_ax.width` is still ignored, and every day is still
               full width. `working` still decides the label colour too; it is
               simply no longer the only mark. */
            const isToday14 = ds === todayStr;
            const dayEvs14 = stripEvents(ds)
              .filter((ev: { type?: string | null }) => eventTypeVisible(agTypeFilt, ev))
              .sort((a: any, b: any) => (a.time || "").localeCompare(b.time || ""));
            const dd14 = String(d.getDate()).padStart(2, "0");
            const mm14 = String(d.getMonth() + 1).padStart(2, "0");
            return (
              <div
                key={ds}
                style={{
                  flex: 2,
                  minWidth: STRIP14_COL,
                  flexShrink: 0,
                  /* 133 §2 — TODAY still outranks the field, exactly as it does
                     in the agenda (132 §7): the accent wash says "you are
                     here", which is a stronger claim than "the firm is
                     closed". `--nwd` is the agenda's own token; the ordinary
                     day keeps the wash it has always had. */
                  background: isToday14
                    ? "var(--as)"
                    : shadeOff
                      ? "var(--nwd)"
                      : "rgba(255,255,255,.02)",
                  /* ── 148 §3.1 — TODAY'S BORDER IS THE STRONGEST LINE IN THE
                     STRIP, MEASURED. Reported as "today's highlighted day,
                     whose border is cut", at every text size. The advisor's
                     mechanism was that this is the only SEMI-TRANSPARENT
                     border here, and that a 1px line at 50% alpha on a box
                     whose width is fractional under a fractional root `zoom`
                     gets painted across two device rows at half intensity —
                     reading as gone while the opaque neighbours survive.
                     REFUTED, off the decoded SCREENSHOT rather than off the
                     DOM (`borderTopWidth` is what was declared, not what was
                     painted). 16 readings — both strips × Petit/Moyen/Grand/
                     Très grand × dark and light — and every one of them says
                     the same thing: all four edges painted, today's peak
                     contrast against its own interior 103.4 vs the opaque
                     neighbour's 19.3 on dark (536%) and 84.4 vs 53.8 on light
                     (157%). Today is 5.4× MORE visible than every other day,
                     not less.
                     AND THE PHASE IS THE VARIABLE THE HYPOTHESIS NAMES, so it
                     was varied: the cell's fractional left/right landed on
                     0.000, 0.094, 0.250, 0.406, 0.656, 0.813, 0.875 across the
                     sizes — including EXACTLY 0 at Moyen, where the zoom is 1
                     and there is no fractional phase at all — and the peak did
                     not move by one unit. At dpr 2 today and its neighbour
                     spread over two device rows IDENTICALLY, both at full
                     intensity: that is a 1px border being ~2 device px wide,
                     not an alpha being halved.
                     SO NOTHING IS CHANGED HERE. The reported cut did not
                     reproduce in the only engine this box can run — chromium.
                     WebKit is installed but will not launch (20+ host
                     libraries absent, no passwordless sudo), so SAFARI IS
                     UNMEASURED, and Laurent reads this app in Safari. That is
                     the open half, and it is his eye that closes it.
                     THE IDENTICAL EXPRESSION LIVES AT THREE SITES, not the two
                     the brief named: here, `Dashboard.tsx` and
                     `AgendaView.tsx`. Whatever ever changes it changes three. */
                  border: `1px solid ${isToday14 ? "color-mix(in srgb, var(--ac) 50%, transparent)" : "var(--br)"}`,
                  borderRadius: RAD.r8,
                  padding: "6px 7px",
                  display: "flex",
                  flexDirection: "column",
                  gap: 3,
                  minHeight: 90,
                  maxHeight: 200,
                  overflowY: "auto",
                }}
              >
                <div
                  style={{
                    fontSize: 9.5,
                    fontWeight: 600,
                    color: isToday14
                      ? "var(--ac)"
                      : isOff
                        ? "var(--tm)"
                        : "var(--ts)",
                    marginBottom: 2,
                    textAlign: "center",
                    cursor: "pointer",
                    lineHeight: 1.3,
                    flexShrink: 0,
                  }}
                  /* 132 §6 — the NAME is on hover and is not printed. The cell
                     is 80px wide; "Onze-Lieve-Vrouw-Hemelvaart" is not a thing
                     it can hold, and truncating a holiday to five letters is
                     worse than a tooltip. */
                  title={isHol ? _holName : undefined}
                  onClick={() => navAgenda()}
                >
                  <span style={{ display: "block" }}>{_dnFmt14.format(d)}</span>
                  <span
                    style={{
                      display: "block",
                      fontWeight: 400,
                      fontFamily: "'JetBrains Mono',monospace",
                      fontSize: 8.5,
                    }}
                  >
                    {dd14}/{mm14}
                  </span>
                </div>
                {dayEvs14.map((ev: any) => {
                  const cs14 = stripMatterOf(ev);
                  const word14 = cs14
                    ? cs14.title.split(/\s+/)[0]
                    : (ev.title || "").split(/\s+/)[0];
                  const t14 = (evtTypes || []).find(
                    (x: any) => x.code === ev.type,
                  ) || { code: ev.type || "?", color: CT.c_8892a4 };
                  const rv = parseInt(t14.color.slice(1, 3), 16) || 136;
                  const gv = parseInt(t14.color.slice(3, 5), 16) || 146;
                  const bv = parseInt(t14.color.slice(5, 7), 16) || 164;
                  return (
                    <div
                      key={ev.id}
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: 2,
                        cursor: "pointer",
                        overflow: "hidden",
                        minHeight: 15,
                        flexShrink: 0,
                      }}
                      onClick={() => stripClick(ev, cs14)}
                      title={cs14 ? cs14.title + " — " + cs14.ref : ev.title || ""}
                    >
                      <span
                        style={{
                          flexShrink: 0,
                          fontSize: FS.s8,
                          padding: "1px 3px",
                          borderRadius: RAD.r3,
                          background: `rgba(${rv},${gv},${bv},.18)`,
                          color: t14.color,
                          fontWeight: 600,
                          fontFamily: "'JetBrains Mono',monospace",
                          whiteSpace: "nowrap",
                          maxWidth: "none",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                        }}
                      >
                        {t14.code}
                      </span>
                      {/* 132 §3 — `!isWknd &&` stood here. The word was dropped
                          because a half-width cell could not hold it; the cell
                          is full width now, so it is rendered on every day. */}
                      <span
                        style={{
                          fontSize: FS.s9,
                          color: "var(--ts)",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                          flex: 1,
                          minWidth: 24,
                        }}
                      >
                        {word14}
                      </span>
                    </div>
                  );
                })}
              </div>
            );
          })}
        </div>
        </HScroll>
      </div>
    );
  };

  // ── the export declarations, side by side so their diff stays readable ────
  const exportSheet = cc
    ? {
        sheetName: "CollectiveCases",
        filename: () => `CollectiveCases ${new Date().toISOString().slice(0, 10)}.xlsx`, // 181 (ruling 1)
        /* LEGAL-LANE fix 2 — the roster leaves the list export as ClientIds
           (bounded by construction); per-member payment detail stays in the
           CC detail view's own exportClientsXlsx. */
        row: (c: any) => ({
          // 214 addendum 5 §29 [RULED 65; ADVISOR — as the clients sheet] — each matter's Id, the FIRST column: a row
          // without a Ref is matched by it on the way back in (src/lib/sheetColumns.ts, `sheetRowMatch`), so a matter
          // without a ref is no longer created again by a round trip.
          Id: (c as { id?: number }).id ?? "",
          Ref: c.ref || "",
          Title: c.title || "",
          Status: c.status || "",
          Type: c.ctype || "",
          OpenDate: c.openDate || "",
          Description: c.desc || "",
          JurisdictionId: c.juId || "",
          // 214 addendum 3 §21 [ruling 62 (b)] — EVERY responsible partner (a junction, several allowed — the form's
          // multi-select); the cell held the FIRST alone and a round trip dropped the others. The plural header, as
          // every other id column; the import reads the singular one too, so an older sheet still imports.
          ResponsiblePartnerIds: (c.responsiblePartners || []).join(","),
          ReferenceHourlyRate: c.referenceHourlyRate ?? "",
          CostsRatio: c.costsRatio ?? "",
          ClaimAmount: c.claimAmount ?? "",
          SuccessFeeRate: c.successFeeRate ?? "",
          ClientIds: (c.clientLinks || []).map((lk: any) => lk.clId).join(","),
          ClientCount: (c.clientLinks || []).length,
          // 214 addendum 2 §18 / addendum 3 §21 — the IDS of the three element-shaped party columns: a collective
          // case's `opIds`, `ocuIds` and `op2Ids` are elements ({ id, roles, … }), and `join` wrote « [object Object] »
          // per party; the import merges the ids back into the stored elements (CollectiveCaseView's importer).
          OpponentIds: elementIdsOf(c.opIds).join(","),
          OpponentLawyerIds: elementIdsOf(c.ocuIds).join(","),
          ExpertIds: (c.exIds || []).join(","),
          ClientContactIds: (c.cpIds || []).join(","),
          OpponentContactIds: (c.ocIds || []).join(","),
          MediatorIds: (c.meIds || []).join(","),
          ArbitratorIds: (c.arIds || []).join(","),
          Op2Ids: elementIdsOf(c.op2Ids).join(","),
          CommitteeIds: (c.committeeIds || []).join(","),
          CaseCollaborators: JSON.stringify(c.caseCollaborators || []),
          Stages: JSON.stringify(c.stages || []),
          Docs: JSON.stringify(c.docs || []),
        }),
        exportedToast: (_ctx: any, n: number) => t("cc.toast.exported", { count: n }),
        failedToast: (_ctx: any, msg: string) =>
          t("cc.toast.export_failed", {
            msg,
            fallback: "⚠ Export failed: {msg}",
          }),
      }
    : {
        sheetName: "Cases",
        filename: () => `Cases ${new Date().toISOString().slice(0, 10)}.xlsx`, // 181 (ruling 1)
        /* LEGAL-LANE (round 2, Excel sweep) — VISIBLE ROWS ONLY is the ruled
           semantics and is now the engine's own rule (EntityList exports fi).
           55 row 10 — ocuIds, never olIds: that column was empty in every
           file this button wrote for 117 days. */
        row: (c: any) => ({
          Id: (c as { id?: number }).id ?? "", // 214 addendum 5 §29 [RULED 65] — the matter's Id first, as the collective sheet
          Ref: c.ref || "",
          Title: c.title || "",
          Status: c.status || "",
          Type: c.ctype || "",
          OpenDate: c.openDate || "",
          Description: c.desc || "",
          JurisdictionId: c.juId || "",
          // 214 addendum 3 §21 [ruling 62 (b)] — every responsible partner, as the collective sheet writes them.
          ResponsiblePartnerIds: (c.responsiblePartners || []).join(","),
          ReferenceHourlyRate: c.referenceHourlyRate ?? "",
          CostsRatio: c.costsRatio ?? "",
          ClaimAmount: c.claimAmount ?? "",
          SuccessFeeRate: c.successFeeRate ?? "",
          ClientIds: (c.clIds || []).join(","),
          OpponentIds: (c.opIds || []).join(","),
          OpponentLawyerIds: (c.ocuIds || []).join(","),
          ExpertIds: (c.exIds || []).join(","),
          ClientContactIds: (c.cpIds || []).join(","),
          OpponentContactIds: (c.ocIds || []).join(","),
          MediatorIds: (c.meIds || []).join(","),
          ArbitratorIds: (c.arIds || []).join(","),
          Op2Ids: (c.op2Ids || []).join(","),
          CaseCollaborators: JSON.stringify(c.caseCollaborators || []),
          Stages: JSON.stringify(c.stages || []),
          Docs: JSON.stringify(c.docs || []),
        }),
        exportedToast: (_ctx: any, n: number) =>
          t("case.toast.exported_cases", { count: n }),
        failedToast: (_ctx: any, msg: string) =>
          t("toast.export_failed", {
            msg,
            fallback: "⚠ Export failed: {msg}",
          }),
      };

  return {
    items: rows,
    et: kind,
    am: kind === "cc" ? null : "ca",
    /* BRIEF 108 §9 — both matter lists ride this one builder, so the class
       that lifts the 28ch cell cap is declared here once and covers C and CC. */
    tableClass: "elist-matters",
    // [R] — the switchboard applies at the column: a withheld or hidden key
    // drops the column, header and cells together, on both lists.
    cols: { t: (colsT as { k: string }[]).filter((col) => columnShown(col.k)), d: [] },
    rowsSource,
    sortCtl: {
      k: sort.k,
      d: sort.d,
      onClick: (k: string) => setSort({ k, d: sort.k === k ? -sort.d : 1 }),
    },
    plainRows: {
      rowStyle: (c: any) =>
        c.status === "archived" ? { opacity: 0.45, filter: "saturate(0)" } : {},
    },
    toolbar: {
      chips: {
        value: chipsValue,
        options: [
          ["all", t("common.all")],
          ["active", t("costs.status.active")],
          ["pending", t("case.status.pending")],
          ["closed", t("costs.status.closed")],
          ["archived", t("case.status.archived")],
        ],
        onChange: onChips,
        counts: {
          all: rows.filter((c: any) => c.status !== "archived").length,
          active: rows.filter((c: any) => c.status === "active").length,
          pending: rows.filter((c: any) => c.status === "pending").length,
          closed: rows.filter((c: any) => c.status === "closed").length,
          archived: rows.filter((c: any) => c.status === "archived").length,
        },
      },
      myToggle: myToggle
        ? {
            label: () => t("case.filter.my_cases", { fallback: "My cases" }),
            active: myToggle.active,
            onToggle: myToggle.onToggle,
            count: myToggle.count,
          }
        : null,
      /* 139 §2.3 — the three-state toggle, produced beside the pill whose
         shape it copies. Rendered by EntityList as a SECOND PeriodFilter —
         the tree's own n-state pill (SharedUI.tsx:190), the same primitive
         the chips above render — so three states cost no new control and no
         approximation by boolean. Counts are over `rows` whole, exactly as
         `myToggle.count` is, so the two pills agree on what they count. */
      prospectToggle: prospectFilter
        ? {
            value: prospectFilter.value,
            onChange: prospectFilter.onChange,
            options: prospectFilterOptions(t),
            counts: prospectFilterCounts(rows),
          }
        : null,
      search: { value: searchText, onChange: onSearch },
      importCtl: { onOpen: onImportOpen },
      addCtl: {
        can: canCreate,
        label: () => (kind === "cc" ? t("cc.add_new") : t("case.add.new")),
        onAdd,
        /* 139 §2.5 — the second entry. `addCtl` is WIDENED, not forked: the
           three existing keys keep their meaning and a caller that passes no
           `onAddProspect` renders byte-identically (`second: null`), which is
           how the single-button form keeps working. One producer (here), one
           consumer (EntityList.tsx), measured. */
        second: onAddProspect
          ? {
              label: () =>
                t("matters.prospect.add.new", { fallback: "New prospect" }),
              onAdd: onAddProspect,
            }
          : null,
      },
      lockCtl: { locked, onToggle: onLockToggle },
      /* 212 §27 — one declared control, the toolbar WIDENED as `addCtl` was
         (139 §2.5), never forked; EntityList places it just left of the add
         button(s) in the actions row (213B §21 [RULED 49]; 212 put it after
         « Importer », which now stands at the filter row's right end). */
      checkCtl: folderCheckCtl(t as TFn, onFolderCheck),
    },
    preList,
    exportSheet,
  };
};
